// src/components/ChatFeed.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import { firestore, collection, query, orderBy, limit, onSnapshot, doc, getDoc, addDoc } from '../firebase-config';
import { AuthContext } from '../AuthContext';
import './ChatFeed.css';

const ChatFeed = () => {
  const { verifiedPlayer, isVerified } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [playerColors, setPlayerColors] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  useEffect(() => {
    const messagesRef = collection(firestore, 'chat');
    const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(50));

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const msgs = [];
      const uuidsToFetch = new Set();

      snapshot.forEach((doc) => {
        const msgData = { id: doc.id, ...doc.data() };
        msgs.push(msgData);
        if (msgData.senderUUID) {
          uuidsToFetch.add(msgData.senderUUID);
        }
      });

      setMessages(msgs.reverse());

      // Fetch current player data for all unique UUIDs
      const colorData = {};
      for (const uuid of uuidsToFetch) {
        if (!playerColors[uuid]) {
          try {
            const playerDoc = await getDoc(doc(firestore, 'players', uuid));
            if (playerDoc.exists()) {
              const data = playerDoc.data();
              colorData[uuid] = {
                nameColour: data.nameColour || '§f',
                icon: data.icon || '',
                rankPrefix: data.rankPrefix || ''
              };
            }
          } catch (err) {
            console.error('Error fetching player data:', err);
          }
        }
      }

      setPlayerColors(prev => ({ ...prev, ...colorData }));
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !isVerified || sending) return;

    setSending(true);
    try {
      await addDoc(collection(firestore, 'chat'), {
        senderUUID: verifiedPlayer.uuid,
        senderName: verifiedPlayer.username,
        message: newMessage.trim(),
        timestamp: Date.now(),
        source: 'website',
        rankPrefix: verifiedPlayer.rankPrefix || '',
        icon: verifiedPlayer.icon || '',
        nameColour: verifiedPlayer.nameColour || '§f'
      });
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const convertMinecraftColor = (code) => {
    const colorMap = {
      '&0': '#000000', '&1': '#0000AA', '&2': '#00AA00', '&3': '#00AAAA',
      '&4': '#AA0000', '&5': '#AA00AA', '&6': '#FFAA00', '&7': '#AAAAAA',
      '&8': '#555555', '&9': '#5555FF', '&a': '#55FF55', '&b': '#55FFFF',
      '&c': '#FF5555', '&d': '#FF55FF', '&e': '#FFFF55', '&f': '#FFFFFF',
      '§0': '#000000', '§1': '#0000AA', '§2': '#00AA00', '§3': '#00AAAA',
      '§4': '#AA0000', '§5': '#AA00AA', '§6': '#FFAA00', '§7': '#AAAAAA',
      '§8': '#555555', '§9': '#5555FF', '§a': '#55FF55', '§b': '#55FFFF',
      '§c': '#FF5555', '§d': '#FF55FF', '§e': '#FFFF55', '§f': '#FFFFFF'
    };
    return colorMap[code] || '#FFFFFF';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const stripColorCodes = (text) => {
    if (!text) return '';
    return text.replace(/[§&$][0-9a-fk-or]/gi, '');
  };

  const getSourceBadge = (source) => {
    const badges = {
      'minecraft': { label: 'MC', color: '#379237' },
      'discord': { label: 'DC', color: '#5865F2' },
      'website': { label: 'WEB', color: '#5dade2' }
    };
    return badges[source] || { label: source?.toUpperCase() || 'MC', color: '#AAAAAA' };
  };

  return (
    <div className="chat-feed">
      <div className="chat-header">
        <h3>Live Chat</h3>
        <span className="chat-status">● Live</span>
      </div>

      <div className="chat-messages" ref={chatContainerRef}>
        {messages.map((msg) => {
          const playerData = playerColors[msg.senderUUID] || {};
          const nameColor = playerData.nameColour || msg.nameColour || '§f';
          const icon = playerData.icon || msg.icon || '';
          const sourceBadge = getSourceBadge(msg.source);

          return (
            <div key={msg.id} className="chat-message">
              <span className="chat-time">{formatTimestamp(msg.timestamp)} </span>
              <span 
                className="chat-source-badge" 
                style={{ backgroundColor: sourceBadge.color }}
              >
                {sourceBadge.label}
              </span>
              <span className="chat-user">
                {icon && (
                  <span 
                    className="user-icon" 
                    style={{ color: convertMinecraftColor(icon.substring(0, 2))}}
                  >
                  {stripColorCodes(icon)}{' '}
                  </span>
                )}
                <span style={{ color: convertMinecraftColor(nameColor) }}>
                  {msg.senderName}
                </span>
              </span>
              <span className="chat-text">: {msg.message}</span>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {isVerified ? (
        <form className="chat-input" onSubmit={handleSendMessage}>
          <input
            type="text"
            placeholder="Send a message to Minecraft..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            maxLength={256}
            disabled={sending}
          />
          <button type="submit" disabled={!newMessage.trim() || sending}>
            {sending ? 'Sending...' : 'Send'}
          </button>
        </form>
      ) : (
        <div className="chat-login-prompt">
          <p>Verify your account to chat</p>
        </div>
      )}
    </div>
  );
};

export default ChatFeed;